import type { Building } from '../../shared/types.js';
import { polyArea } from '../../shared/geom.js';

/**
 * Where one building's wall stands in the same place as another's, and only one of them
 * should be drawn.
 *
 * OSM is full of footprints that overlap: a wing mapped as a `building` inside the outline
 * of the whole (it should be `building:part`, which is never drawn — DECISIONS.md §15), a
 * block redrawn over an older one, two halves of a terrace that share a side and were both
 * traced along it. Where two of those walls run along one line and face the same way, both
 * extrude to the same plane, each with its own windows, and the depth buffer cannot say
 * which is in front: the facade flickers between two window patterns as the camera moves.
 * A wall facing the other way is the far side of a party wall and is culled, so it never
 * fights.
 *
 * A stretch of wall is left out wherever another building's wall stands along the same line,
 * facing the same way, from at least as low to at least as high. That is the only case in
 * which leaving it out cannot open a hole: the other wall covers every pixel it would have
 * drawn. Between two walls of the same height the larger building keeps its wall, so an
 * outline reads as one facade all the way round rather than changing pattern where a wing
 * was traced along it.
 *
 * "As low" is measured against the ground along the stretch, not against each wall's own
 * foot. A building stands on the lowest ground under its footprint, so two overlapping
 * footprints on a slope start their walls at different heights, both of them underground at
 * the wall they share. Compared foot to foot, neither covered the other, and in Leszno that
 * was two thirds of what still fought.
 */

/** Further apart than this, two walls are two walls; nearer, they share pixels at a distance. */
export const SHARED_WALL_TOLERANCE = 0.15;
/** A sliver of wall shorter than this is not worth two triangles. */
const MIN_PIECE = 0.05;
/**
 * About two and a half degrees. Two walls further apart in angle than this only come within
 * the tolerance of each other for a few metres around where they cross, and leaving one out
 * there would open a wedge between them.
 */
const MIN_PARALLEL = 0.999;
const CELL = 24;
/** Grid cells packed into one number; a region is a few hundred cells across. */
const ROW = 1 << 16;
/** How often the ground is read along a stretch, to find where it is lowest. */
const GROUND_STEP = 4;

/** The vertical extent of one building's walls, terrain lift included. */
export interface WallSpan {
  bottom: number;
  top: number;
}

interface Edge {
  building: number;
  index: number;
  ax: number;
  az: number;
  /** Unit direction along the edge. */
  dx: number;
  dz: number;
  len: number;
}

/**
 * For each building, by index, the stretches of each edge that another wall covers: edge
 * index to `[from, to]` in metres from the edge's first vertex, merged and sorted. Buildings
 * and edges with nothing covered are absent.
 */
export function coveredWalls(
  buildings: Building[],
  spans: WallSpan[],
  groundAt: (x: number, z: number) => number = () => -Infinity,
): Map<number, Map<number, [number, number][]>> {
  const edges: Edge[] = [];
  const grid = new Map<number, number[]>();
  for (let b = 0; b < buildings.length; b++) {
    const poly = buildings[b].poly;
    const n = poly.length / 2;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const ax = poly[i * 2];
      const az = poly[i * 2 + 1];
      const len = Math.hypot(poly[j * 2] - ax, poly[j * 2 + 1] - az);
      if (len < 1e-3) continue;
      const e = edges.length;
      edges.push({ building: b, index: i, ax, az, dx: (poly[j * 2] - ax) / len, dz: (poly[j * 2 + 1] - az) / len, len });
      const x0 = Math.floor((Math.min(ax, poly[j * 2]) - SHARED_WALL_TOLERANCE) / CELL);
      const x1 = Math.floor((Math.max(ax, poly[j * 2]) + SHARED_WALL_TOLERANCE) / CELL);
      const z0 = Math.floor((Math.min(az, poly[j * 2 + 1]) - SHARED_WALL_TOLERANCE) / CELL);
      const z1 = Math.floor((Math.max(az, poly[j * 2 + 1]) + SHARED_WALL_TOLERANCE) / CELL);
      for (let x = x0; x <= x1; x++) {
        for (let z = z0; z <= z1; z++) {
          const key = x * ROW + z;
          const list = grid.get(key);
          if (list) list.push(e);
          else grid.set(key, [e]);
        }
      }
    }
  }

  const areas = buildings.map((b) => Math.abs(polyArea(b.poly)));
  /** Whether `other`'s wall hides `self`'s along a stretch where the ground is no lower than `ground`. */
  const hides = (other: number, self: number, ground: number): boolean => {
    const o = spans[other];
    const s = spans[self];
    const oBottom = Math.max(o.bottom, ground);
    const sBottom = Math.max(s.bottom, ground);
    const covers = oBottom <= sBottom + 0.01 && o.top >= s.top - 0.01;
    if (!covers) return false;
    const mutual = sBottom <= oBottom + 0.01 && s.top >= o.top - 0.01;
    if (!mutual) return true;
    return areas[other] > areas[self] || (areas[other] === areas[self] && buildings[other].id < buildings[self].id);
  };

  const out = new Map<number, Map<number, [number, number][]>>();
  // The last edge each candidate was tested against: an edge spans several cells.
  const seen = new Int32Array(edges.length).fill(-1);
  for (let me = 0; me < edges.length; me++) {
    const self = edges[me];
    const stretches: [number, number][] = [];
    const ex = self.ax + self.dx * self.len;
    const ez = self.az + self.dz * self.len;
    const x0 = Math.floor((Math.min(self.ax, ex) - SHARED_WALL_TOLERANCE) / CELL);
    const x1 = Math.floor((Math.max(self.ax, ex) + SHARED_WALL_TOLERANCE) / CELL);
    const z0 = Math.floor((Math.min(self.az, ez) - SHARED_WALL_TOLERANCE) / CELL);
    const z1 = Math.floor((Math.max(self.az, ez) + SHARED_WALL_TOLERANCE) / CELL);
    for (let x = x0; x <= x1; x++) {
      for (let z = z0; z <= z1; z++) {
        for (const e of grid.get(x * ROW + z) ?? []) {
          if (seen[e] === me) continue;
          seen[e] = me;
          const other = edges[e];
          if (other.building === self.building) continue;
          // Same direction along a ring wound the same way is the same outward face.
          if (other.dx * self.dx + other.dz * self.dz < MIN_PARALLEL) continue;
          // The other edge in this one's frame: `p` along it, `q` out from it.
          const ox = other.ax + other.dx * other.len;
          const oz = other.az + other.dz * other.len;
          const p0 = (other.ax - self.ax) * self.dx + (other.az - self.az) * self.dz;
          const p1 = (ox - self.ax) * self.dx + (oz - self.az) * self.dz;
          const q0 = (other.ax - self.ax) * self.dz - (other.az - self.az) * self.dx;
          const q1 = (ox - self.ax) * self.dz - (oz - self.az) * self.dx;
          let t0 = Math.max(0, p0);
          let t1 = Math.min(self.len, p1);
          // Only where the two are within the tolerance of each other, which for a long edge
          // a fraction of a degree off this one's bearing is not all the way along it.
          const slope = (q1 - q0) / (p1 - p0);
          if (Math.abs(slope) > 1e-9) {
            const ta = p0 + (-SHARED_WALL_TOLERANCE - q0) / slope;
            const tb = p0 + (SHARED_WALL_TOLERANCE - q0) / slope;
            t0 = Math.max(t0, Math.min(ta, tb));
            t1 = Math.min(t1, Math.max(ta, tb));
          } else if (Math.abs(q0) > SHARED_WALL_TOLERANCE) {
            continue;
          }
          if (t1 - t0 < MIN_PIECE) continue;
          if (!hides(other.building, self.building, lowestAlong(self, t0, t1, groundAt))) continue;
          stretches.push([t0, t1]);
        }
      }
    }
    if (stretches.length === 0) continue;
    stretches.sort((a, b) => a[0] - b[0]);
    const merged: [number, number][] = [stretches[0]];
    for (const s of stretches.slice(1)) {
      const last = merged[merged.length - 1];
      if (s[0] <= last[1] + MIN_PIECE) last[1] = Math.max(last[1], s[1]);
      else merged.push(s);
    }
    let byEdge = out.get(self.building);
    if (!byEdge) out.set(self.building, (byEdge = new Map()));
    byEdge.set(self.index, merged);
  }
  return out;
}

function lowestAlong(e: Edge, from: number, to: number, groundAt: (x: number, z: number) => number): number {
  const steps = Math.max(1, Math.ceil((to - from) / GROUND_STEP));
  let lowest = Infinity;
  for (let k = 0; k <= steps; k++) {
    const t = from + ((to - from) * k) / steps;
    lowest = Math.min(lowest, groundAt(e.ax + e.dx * t, e.az + e.dz * t));
  }
  return lowest;
}

/**
 * What is left of an edge `len` long once the covered stretches are taken out, as `[from,
 * to]` in metres. Gaps narrower than `MIN_PIECE` are closed rather than drawn.
 */
export function uncovered(len: number, covered: [number, number][] | undefined): [number, number][] {
  if (!covered || covered.length === 0) return [[0, len]];
  const pieces: [number, number][] = [];
  let at = 0;
  for (const [from, to] of covered) {
    if (from - at >= MIN_PIECE) pieces.push([at, from]);
    at = Math.max(at, to);
  }
  if (len - at >= MIN_PIECE) pieces.push([at, len]);
  return pieces;
}
